import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { newUserService } from '../../services/newUserService';
import { newInstituteService } from '../../services/newInstituteService';
import { courseService } from '../../services/courseService';
import { newBatchService } from '../../services/newBatchService';
import { CLASS_OPTIONS } from '../../utils/classOptions';
import { getDashboardRoute } from '../../utils/roleBasedRouting';
import { isValidEmail, isValidPhone } from '../../utils/validation';
import StudentEnrollmentFields from '../../components/enrollment/StudentEnrollmentFields';
import PageLayout from '../../components/layout/PageLayout';
import Input from '../../components/ui/Input';
import DateTimePicker from '../../components/ui/DateTimePicker';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';

// ---- Small presentational helpers ---------------------------------------
// Shared with the former CreateUserModal — keep the form markup declarative
// and consistent with the app's design tokens.

const FIELD_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const FieldLabel = ({ htmlFor, required, children }) => (
  <label htmlFor={htmlFor} className="text-sm font-medium leading-none text-foreground">
    {children}
    {required && <span className="text-destructive ml-1">*</span>}
  </label>
);

const SelectField = ({ label, required, className, children, ...props }) => (
  <div className="space-y-2">
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <select className={cn(FIELD_CLASS, className)} {...props}>
      {children}
    </select>
  </div>
);

const TextareaField = ({ label, required, rows = 3, className, ...props }) => (
  <div className="space-y-2">
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <textarea
      rows={rows}
      className={cn(
        'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y',
        className
      )}
      {...props}
    />
  </div>
);

// A titled group of fields with an icon badge. Sections after the first are
// separated by a subtle divider (handled by the parent container).
const Section = ({ icon, title, description, children }) => (
  <section className="space-y-4">
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon name={icon} size={16} />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-foreground leading-tight">{title}</h3>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
    </div>
    {children}
  </section>
);

// Responsive two-column grid that collapses to one column on narrow screens.
const TwoCol = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const FORM_ID = 'user-form';

/**
 * Full-page create/edit form for Student / Teacher / Institute-Admin users.
 * Replaces the former CreateUserModal — the 30+ field form had outgrown a modal.
 *
 * Context is passed in via route state from the launching page:
 *   navigate('/user-form', { state: {
 *     userRole, defaultInstituteId, defaultInstitute, editMode, existingUser, returnTo
 *   }})
 * On submit it returns to `returnTo` (which remounts and reloads its list).
 */
const UserForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();

  const state = location.state || {};
  const {
    userRole = 'STUDENT',
    defaultInstituteId = '',
    defaultInstitute = null,
    editMode = false,
    existingUser = null,
    returnTo
  } = state;

  // Without route state we have no context (e.g. page was refreshed or hit
  // directly) — bounce back to a safe place rather than render an ambiguous form.
  const hasContext = !!location.state && !!state.userRole;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    phone: '',
    instituteId: defaultInstituteId,
    role: userRole,
    // Teacher-specific fields
    department: '',
    subject: '',
    qualification: '',
    experienceYears: 0,
    specialization: '',
    bio: '',
    enabled: true,
    // Student-specific fields
    course: '',
    currentClass: '',
    rollNumber: '',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    address: '',
    dateOfBirth: '',
    bloodGroup: '',
    emergencyContact: '',
    // Decoupled assignment: independent sets of course ids and batch ids.
    courseIds: [],
    batchIds: []
  });
  const [institutes, setInstitutes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInstitutes, setLoadingInstitutes] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [error, setError] = useState('');
  // Per-field validation messages (email/username/phone format). Surfaced inline
  // under each input so format constraints are caught before a backend 400.
  const [fieldErrors, setFieldErrors] = useState({});

  // Redirect away if there's no context to render against.
  useEffect(() => {
    if (!hasContext) {
      const fallbackRole = (userProfile || user || {}).role;
      navigate(returnTo || getDashboardRoute(fallbackRole), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load institutes (for the dropdown when no institute is pre-assigned).
  useEffect(() => {
    if (hasContext) loadInstitutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load courses + batches for the student assignment dropdowns.
  useEffect(() => {
    if (hasContext && userRole === 'STUDENT') {
      loadCourses();
      loadBatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // Populate the form from the existing user (edit) or defaults (create).
  useEffect(() => {
    if (editMode && existingUser) {
      setFormData({
        firstName: existingUser.firstName || '',
        lastName: existingUser.lastName || '',
        email: existingUser.email || '',
        username: existingUser.username || '',
        password: '', // Don't populate password for security
        phone: existingUser.phone || '',
        instituteId: existingUser.instituteId || defaultInstituteId,
        role: existingUser.role || userRole,
        department: existingUser.department || '',
        subject: existingUser.subject || '',
        qualification: existingUser.qualification || '',
        experienceYears: existingUser.experienceYears || 0,
        specialization: existingUser.specialization || '',
        bio: existingUser.bio || '',
        enabled: existingUser.enabled !== undefined ? existingUser.enabled : true,
        course: existingUser.course || '',
        currentClass: existingUser.currentClass || '',
        rollNumber: existingUser.rollNumber || '',
        parentName: existingUser.parentName || '',
        parentPhone: existingUser.parentPhone || '',
        parentEmail: existingUser.parentEmail || '',
        address: existingUser.address || '',
        dateOfBirth: existingUser.dateOfBirth ? existingUser.dateOfBirth.split('T')[0] : '',
        bloodGroup: existingUser.bloodGroup || '',
        emergencyContact: existingUser.emergencyContact || '',
        // Hydrate the independent course / batch sets from the student's DTOs.
        courseIds: Array.isArray(existingUser.courseEnrollments)
          ? existingUser.courseEnrollments.map((en) => en.courseId).filter((id) => id != null)
          : [],
        batchIds: Array.isArray(existingUser.batchMemberships)
          ? existingUser.batchMemberships.map((m) => m.batchId).filter((id) => id != null)
          : []
      });
    } else {
      setFormData(prev => ({
        ...prev,
        instituteId: defaultInstituteId,
        role: userRole
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInstitutes = async () => {
    setLoadingInstitutes(true);
    try {
      const { data, error } = await newInstituteService.getInstitutes();
      setInstitutes(data && !error && Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load institutes:', err);
      setInstitutes([]);
    } finally {
      setLoadingInstitutes(false);
    }
  };

  const loadCourses = async () => {
    setLoadingCourses(true);
    try {
      const { data, error } = await courseService.getCourses({ page: 0, size: 100 });
      setCourses(!error && Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load courses:', err);
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadBatches = async () => {
    setLoadingBatches(true);
    try {
      const { data, error } = await newBatchService.getAllBatches();
      setBatches(!error && Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load batches:', err);
      setBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const goBack = () => {
    if (returnTo) navigate(returnTo);
    else navigate(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!formData.firstName.trim() || !formData.email.trim()) {
      setError('First name and email are required');
      setLoading(false);
      return;
    }

    // Password is required for new users, optional for updates
    if (!editMode && !formData.password.trim()) {
      setError('Password is required for new users');
      setLoading(false);
      return;
    }

    if (!formData.instituteId) {
      setError('Please select an institute');
      setLoading(false);
      return;
    }

    // Format validation (email/username/phone). Catch these client-side so the
    // user sees the constraint inline instead of as a backend 400.
    const fieldsToValidate = ['email', 'phone'];
    if (!hideAccountFields) fieldsToValidate.push('username');
    if (formData.role === 'STUDENT') {
      fieldsToValidate.push('parentEmail', 'parentPhone', 'emergencyContact');
    }
    const nextFieldErrors = {};
    fieldsToValidate.forEach((field) => {
      const message = validateField(field, formData[field]);
      if (message) nextFieldErrors[field] = message;
    });
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError('Please fix the highlighted fields before submitting.');
      setLoading(false);
      return;
    }

    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        username: formData.username || formData.email,
        phone: formData.phone,
        instituteId: formData.instituteId,
        role: formData.role
      };

      if (formData.password.trim()) {
        userData.password = formData.password;
      }

      // Carry role-specific fields through to the API.
      if (formData.role === 'STUDENT') {
        const courseIds = (formData.courseIds || []).map(Number).filter((n) => !Number.isNaN(n));
        const batchIds = (formData.batchIds || []).map(Number).filter((n) => !Number.isNaN(n));
        const firstCourseName = courses.find((c) => Number(c.id) === courseIds[0])?.name;
        Object.assign(userData, {
          course: firstCourseName || formData.course || '',
          currentClass: formData.currentClass,
          rollNumber: formData.rollNumber,
          parentName: formData.parentName,
          parentPhone: formData.parentPhone,
          parentEmail: formData.parentEmail,
          address: formData.address,
          dateOfBirth: formData.dateOfBirth,
          bloodGroup: formData.bloodGroup,
          emergencyContact: formData.emergencyContact,
          enabled: formData.enabled,
          courseIds,
          batchIds
        });
      } else if (formData.role === 'TEACHER') {
        Object.assign(userData, {
          department: formData.department,
          subject: formData.subject,
          qualification: formData.qualification,
          experienceYears: formData.experienceYears,
          specialization: formData.specialization,
          bio: formData.bio,
          enabled: formData.enabled
        });
      }

      let result;
      if (editMode && existingUser) {
        result = await newUserService.updateUser(existingUser.id, userData);

        // The role-specific update endpoints (PUT /students|teachers/{id}) don't carry
        // email — it lives on the core user record. If the admin changed it, persist it
        // through the user endpoint (the same path the super-admin dashboard uses).
        const newEmail = formData.email.trim();
        const emailChanged =
          !result.error &&
          (userRole === 'STUDENT' || userRole === 'TEACHER') &&
          newEmail &&
          newEmail.toLowerCase() !== (existingUser.email || '').trim().toLowerCase();

        if (emailChanged) {
          const emailResult = await newUserService.updateUserProfile(existingUser.id, {
            email: newEmail,
            username: formData.username || existingUser.username || newEmail,
            firstName: formData.firstName,
            lastName: formData.lastName,
            role: userRole,
            instituteId: formData.instituteId,
          });
          if (emailResult.error) {
            setError(
              (emailResult.error.message || 'The email could not be updated') +
                ' — other changes were saved.'
            );
            setLoading(false);
            return;
          }
        }
      } else {
        result = await newUserService.createUserProfile(userData);
      }

      const { error } = result;
      if (error) {
        setError(error.message || `Failed to ${editMode ? 'update' : 'create'} ${userRole.toLowerCase()}`);
      } else {
        // Success — go back to the launching list (it remounts and reloads).
        navigate(returnTo || getDashboardRoute(userRole), { replace: true });
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(`An unexpected error occurred: ${err.message || err.toString()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear form-level error when user starts typing
    // Clear this field's inline error as the user edits it.
    setFieldErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  // Returns a format-error message for a field, or '' if it's valid. Empty
  // optional fields are always valid here — required-ness is enforced separately.
  const validateField = (field, rawValue) => {
    const value = (rawValue ?? '').toString().trim();
    switch (field) {
      case 'email':
        if (!value) return 'Email is required';
        if (!isValidEmail(value)) return 'Enter a valid email address';
        return '';
      case 'username':
        // Optional, but the backend stores the username as an email, so a
        // non-blank value must be email-format.
        if (value && !isValidEmail(value)) return 'Username must be a valid email';
        return '';
      case 'parentEmail':
        if (value && !isValidEmail(value)) return 'Enter a valid email address';
        return '';
      case 'phone':
      case 'parentPhone':
      case 'emergencyContact':
        if (value && !isValidPhone(value)) return 'Enter a valid phone number';
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => {
    const message = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: message }));
  };

  const generatePassword = () => {
    const password = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-4).toUpperCase();
    handleInputChange('password', password);
  };

  // ---- Derived display values -------------------------------------------
  const roleWord =
    userRole === 'TEACHER' ? 'Teacher' :
    userRole === 'INST_ADMIN' ? 'Institute Admin' :
    'Student';

  // When editing a teacher/student, username + password are immutable account
  // credentials (set at creation) so we hide them. Email stays editable — it's a
  // fallback for fixing a wrong/lost sign-in address — and is persisted via the
  // user endpoint after the role-specific save (see handleSubmit).
  const hideAccountFields = (userRole === 'TEACHER' || userRole === 'STUDENT') && editMode;

  const submitVariant =
    userRole === 'TEACHER' ? 'success' :
    userRole === 'INST_ADMIN' ? 'warning' :
    'default';

  const pageTitle = `${editMode ? 'Edit' : 'Create New'} ${roleWord}`;
  const pageDescription = editMode
    ? `Update the ${roleWord.toLowerCase()}'s details below.`
    : `Fill in the details to add a new ${roleWord.toLowerCase()}.`;

  if (!hasContext) return null;

  return (
    <PageLayout title={pageTitle}>
      <div className="p-4 sm:p-6 pb-28 max-w-3xl mx-auto">
        {/* Header with back button */}
        <div className="mb-6 flex items-start gap-3">
          <button
            type="button"
            onClick={goBack}
            aria-label="Go back"
            className="mt-1 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
              {pageTitle}
            </h1>
            <p className="text-muted-foreground mt-1">{pageDescription}</p>
          </div>
        </div>

        {/* Form card */}
        <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
          <form
            id={FORM_ID}
            onSubmit={handleSubmit}
            noValidate
            className="[&>section+section]:mt-6 [&>section+section]:border-t [&>section+section]:border-border [&>section+section]:pt-6"
          >
            {/* ---- Basic information (all roles) ---- */}
            <Section icon="User" title="Basic Information">
              <TwoCol>
                <Input
                  label="First Name"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="First name"
                  disabled={loading}
                />
                <Input
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Last name"
                  disabled={loading}
                />
              </TwoCol>

              {hideAccountFields ? (
                /* Editing a student/teacher: email stays editable (admin fallback for a
                   wrong/lost address) but username and password remain immutable. */
                <Input
                  type="email"
                  label="Email Address"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  error={fieldErrors.email}
                  placeholder="email@example.com"
                  description="Updates the user's sign-in email in Cognito."
                  disabled={loading}
                />
              ) : (
                <TwoCol>
                  <Input
                    type="email"
                    label="Email Address"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    error={fieldErrors.email}
                    placeholder="email@example.com"
                    disabled={loading}
                  />
                  <Input
                    label="Username"
                    description="Optional — must be a valid email; leave blank to use the email above"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    onBlur={() => handleBlur('username')}
                    error={fieldErrors.username}
                    placeholder="name@example.com"
                    disabled={loading}
                  />
                </TwoCol>
              )}

              <TwoCol>
                <Input
                  type="tel"
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  onBlur={() => handleBlur('phone')}
                  error={fieldErrors.phone}
                  placeholder="e.g. +1 555 123 4567"
                  description="Digits, spaces, + and - only"
                  disabled={loading}
                />

                {/* Institute */}
                <div className="space-y-2">
                  <FieldLabel required>Institute</FieldLabel>
                  {defaultInstituteId ? (
                    <div className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border bg-muted px-3 text-sm text-foreground">
                      <span className="truncate">
                        {(() => {
                          if (defaultInstitute && defaultInstitute.name) {
                            return `${defaultInstitute.name} (${defaultInstitute.code || defaultInstitute.instituteCode || 'N/A'})`;
                          }
                          const selectedInstitute = institutes.find((inst) => inst.id === formData.instituteId);
                          return selectedInstitute
                            ? `${selectedInstitute.name} (${selectedInstitute.code})`
                            : loadingInstitutes ? 'Loading institute...' : 'Institute Auto-Selected';
                        })()}
                      </span>
                      <span className="flex-shrink-0 text-xs text-muted-foreground">Auto-assigned</span>
                    </div>
                  ) : (
                    <select
                      value={formData.instituteId}
                      onChange={(e) => handleInputChange('instituteId', e.target.value)}
                      className={FIELD_CLASS}
                      disabled={loading || loadingInstitutes}
                    >
                      <option value="">
                        {loadingInstitutes ? 'Loading institutes...' : 'Select an institute'}
                      </option>
                      {institutes.map((institute) => (
                        <option key={institute.id} value={institute.id}>
                          {institute.name} ({institute.code})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </TwoCol>

              {!hideAccountFields && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <FieldLabel htmlFor="cu-password" required>Password</FieldLabel>
                    <button
                      type="button"
                      onClick={generatePassword}
                      disabled={loading}
                      className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                    >
                      Generate
                    </button>
                  </div>
                  <input
                    id="cu-password"
                    type="text"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={FIELD_CLASS}
                    placeholder="Password"
                    disabled={loading}
                  />
                </div>
              )}
            </Section>

            {/* ---- Teacher-specific ---- */}
            {userRole === 'TEACHER' && (
              <Section icon="Briefcase" title="Professional Details">
                <TwoCol>
                  <Input
                    label="Department"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    placeholder="Department"
                    disabled={loading}
                  />
                  <Input
                    label="Subject"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Subject"
                    disabled={loading}
                  />
                </TwoCol>

                <TwoCol>
                  <Input
                    label="Qualification"
                    value={formData.qualification}
                    onChange={(e) => handleInputChange('qualification', e.target.value)}
                    placeholder="e.g., M.Sc., B.Tech."
                    disabled={loading}
                  />
                  <Input
                    type="number"
                    label="Experience (years)"
                    min="0"
                    value={formData.experienceYears}
                    onChange={(e) => handleInputChange('experienceYears', parseInt(e.target.value) || 0)}
                    placeholder="0"
                    disabled={loading}
                  />
                </TwoCol>

                <Input
                  label="Specialization"
                  value={formData.specialization}
                  onChange={(e) => handleInputChange('specialization', e.target.value)}
                  placeholder="Area of specialization"
                  disabled={loading}
                />

                <TextareaField
                  label="Bio"
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  placeholder="Brief bio or introduction"
                  disabled={loading}
                />
              </Section>
            )}

            {/* ---- Student-specific ---- */}
            {userRole === 'STUDENT' && (
              <>
                <Section icon="GraduationCap" title="Academic Information">
                  <TwoCol>
                    <SelectField
                      label="Current Class"
                      value={formData.currentClass}
                      onChange={(e) =>
                        handleInputChange('currentClass', e.target.value === '' ? '' : parseInt(e.target.value, 10))
                      }
                      disabled={loading}
                    >
                      <option value="">Select class</option>
                      {CLASS_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </SelectField>
                    <Input
                      label="Roll Number"
                      value={formData.rollNumber}
                      onChange={(e) => handleInputChange('rollNumber', e.target.value)}
                      placeholder="Student roll number"
                      disabled={loading}
                    />
                  </TwoCol>
                </Section>

                <Section
                  icon="BookOpen"
                  title="Courses & Batches"
                  description="A student may be enrolled in multiple courses and batches."
                >
                  <StudentEnrollmentFields
                    courses={courses}
                    batches={batches}
                    loadingCourses={loadingCourses}
                    loadingBatches={loadingBatches}
                    value={{ courseIds: formData.courseIds, batchIds: formData.batchIds }}
                    onChange={({ courseIds, batchIds }) => {
                      handleInputChange('courseIds', courseIds);
                      handleInputChange('batchIds', batchIds);
                    }}
                    disabled={loading}
                  />
                </Section>

                <Section icon="Users" title="Parent / Guardian">
                  <TwoCol>
                    <Input
                      label="Parent Name"
                      value={formData.parentName}
                      onChange={(e) => handleInputChange('parentName', e.target.value)}
                      placeholder="Parent/Guardian name"
                      disabled={loading}
                    />
                    <Input
                      type="tel"
                      label="Parent Phone"
                      value={formData.parentPhone}
                      onChange={(e) => handleInputChange('parentPhone', e.target.value)}
                      onBlur={() => handleBlur('parentPhone')}
                      error={fieldErrors.parentPhone}
                      placeholder="e.g. +1 555 123 4567"
                      disabled={loading}
                    />
                  </TwoCol>
                  <Input
                    type="email"
                    label="Parent Email"
                    value={formData.parentEmail}
                    onChange={(e) => handleInputChange('parentEmail', e.target.value)}
                    onBlur={() => handleBlur('parentEmail')}
                    error={fieldErrors.parentEmail}
                    placeholder="parent@email.com"
                    disabled={loading}
                  />
                </Section>

                <Section icon="IdCard" title="Personal Information">
                  <TwoCol>
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none text-foreground">Date of Birth</label>
                      <DateTimePicker
                        mode="date"
                        value={formData.dateOfBirth}
                        onChange={(v) => handleInputChange('dateOfBirth', v)}
                        disabled={loading}
                      />
                    </div>
                    <SelectField
                      label="Blood Group"
                      value={formData.bloodGroup}
                      onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </SelectField>
                  </TwoCol>

                  <TextareaField
                    label="Address"
                    rows={2}
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Full address"
                    disabled={loading}
                  />

                  <Input
                    type="tel"
                    label="Emergency Contact"
                    value={formData.emergencyContact}
                    onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                    onBlur={() => handleBlur('emergencyContact')}
                    error={fieldErrors.emergencyContact}
                    placeholder="e.g. +1 555 123 4567"
                    disabled={loading}
                  />
                </Section>
              </>
            )}

            {/* ---- Status (edit only, teacher + student) ---- */}
            {editMode && (userRole === 'TEACHER' || userRole === 'STUDENT') && (
              <Section icon="ToggleRight" title="Account Status">
                <Checkbox
                  label={`Active ${roleWord} Account`}
                  description="Inactive accounts cannot sign in."
                  checked={formData.enabled}
                  onChange={(e) => handleInputChange('enabled', e.target.checked)}
                  disabled={loading}
                />
              </Section>
            )}

            {error && (
              <div className="mt-6 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 z-10 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-end gap-3">
          <Button type="button" variant="ghost" onClick={goBack} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} variant={submitVariant} loading={loading} disabled={loading}>
            {editMode ? `Update ${roleWord}` : `Create ${roleWord}`}
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

export default UserForm;
